import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import './App.css';

// Components
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import JobBoard from './components/JobBoard';
import JobsList from './components/JobsList';
import JobDetail from './components/JobDetail';
import AdminPanel from './components/AdminPanel';
import UsersManagement from './components/UsersManagement';
import AdminSupportInbox from './components/AdminSupportInbox';
import UserSupportRequests from './components/UserSupportRequests';
import AdminSettings from './components/AdminSettings';
import BusinessLocationSettings from './components/BusinessLocationSettings';
import AdminAddressValidation from './components/AdminAddressValidation';
import AdminJobsManagement from './components/AdminJobsManagement';
import AppLayout from './components/AppLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Verify token validity by fetching profile
      axios.get(`${API}/profile`)
        .then((response) => {
          // Prefer fresh profile data from server if available
          const stored = JSON.parse(localStorage.getItem('user') || '{}');
          setUser({ ...stored, ...response.data });
        })
        .catch(() => {
          logout();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    toast.success('Logged out successfully');
  };

  const register = async (userData) => {
    try {
      await axios.post(`${API}/auth/register`, userData);
      toast.success('Registration successful! Awaiting admin approval.');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, register, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (requireAdmin && !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <AppLayout>{children}</AppLayout>;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/jobs" 
              element={
                <ProtectedRoute>
                  <JobBoard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/jobs-list" 
              element={
                <ProtectedRoute>
                  <JobsList />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/jobs/:jobId" 
              element={
                <ProtectedRoute>
                  <JobDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminPanel />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/users" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <UsersManagement />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/support" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminSupportInbox />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/support/:ticketId" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminSupportInbox />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/support/requests" 
              element={
                <ProtectedRoute>
                  <UserSupportRequests />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/settings" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminSettings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/business/location" 
              element={
                <ProtectedRoute>
                  <BusinessLocationSettings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/addresses" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminAddressValidation />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/jobs" 
              element={
                <ProtectedRoute requireAdmin={true}>
                  <AdminJobsManagement />
                </ProtectedRoute>
              } 
            />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
